import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { TASK_CATEGORIES } from "./scouting/parts";
import { UserPlus, KeyRound, Trash2, ShieldCheck, ListChecks, X } from "lucide-react";

const ROLES = ["admin", "supervisor", "foreman", "irrigator", "employee"];
const WL_ROLES = ["supervisor", "foreman", "irrigator"];
const EMPTY = { username: "", name: "", role: "foreman", password: "", email: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resetFor, setResetFor] = useState(null); // user object
  const [newPass, setNewPass] = useState("");
  const [wlFor, setWlFor] = useState(null);
  const [wlCats, setWlCats] = useState([]);

  const openWl = (u) => { setWlFor(u); setWlCats(u.worklist_categories || []); };
  const saveWl = async () => {
    await api.put(`/users/${wlFor.id}/worklist`, { categories: wlCats });
    setWlFor(null);
    await load();
  };

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data.filter((u) => u.role !== "tractor_driver"));
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const body = { ...form, username: form.username.trim().toLowerCase(), email: form.email.trim() || null };
      await api.post("/auth/register", body);
      setForm(EMPTY);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const doReset = async () => {
    if (!resetFor || newPass.length < 4) { setError("Password must be at least 4 characters"); return; }
    try {
      await api.put(`/users/${resetFor.id}/password`, { new_password: newPass });
      setResetFor(null); setNewPass(""); setError("");
      alert("Password updated.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete login account "${u.username || u.email}"? This cannot be undone.`)) return;
    await api.delete(`/users/${u.id}`);
    await load();
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Users</h1>
      <p className="text-sm text-stone-500 -mt-3">Create login accounts and reset passwords. Users sign in with their username.</p>

      <form onSubmit={create} className="bg-white border border-stone-200 p-6 shadow-sm shadow-stone-900/5 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-stone-500 block mb-1">Username *</label>
          <input data-testid="user-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required autoCapitalize="none" className="w-full h-11 px-3 border border-stone-200" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-stone-500 block mb-1">Full Name *</label>
          <input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full h-11 px-3 border border-stone-200" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-stone-500 block mb-1">Role *</label>
          <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-11 px-3 border border-stone-200 bg-white">
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-stone-500 block mb-1">Password *</label>
          <input data-testid="user-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="w-full h-11 px-3 border border-stone-200" />
        </div>
        <button data-testid="user-create-btn" disabled={busy} className="h-11 px-4 bg-[#1F3D34] text-white font-bold uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-50">
          <UserPlus size={18} /> {busy ? "Creating…" : "Create User"}
        </button>
        {error && <div data-testid="users-error" className="sm:col-span-5 text-sm text-red-700 bg-red-50 border border-red-200 p-2">{error}</div>}
      </form>

      <div className="bg-white border border-stone-200 shadow-sm shadow-stone-900/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b-2 border-stone-300">
            <tr className="text-left tracking-wide text-xs">
              <th className="p-3">Username</th>
              <th className="p-3">Name</th>
              <th className="p-3">Role</th>
              <th className="p-3">Work List Access</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-stone-200" data-testid={`user-row-${u.username || u.id}`}>
                <td className="p-3 font-mono font-bold">{u.username || "—"}</td>
                <td className="p-3">{u.name}</td>
                <td className="p-3"><span className="inline-flex items-center gap-1 text-xs font-bold uppercase"><ShieldCheck size={13} /> {u.role}</span></td>
                <td className="p-3">
                  {u.role === "admin" ? (
                    <span className="text-xs text-stone-400 font-semibold">All types</span>
                  ) : WL_ROLES.includes(u.role) ? (
                    <button data-testid={`user-worklist-${u.username || u.id}`} onClick={() => openWl(u)}
                      className={`h-9 px-3 border font-bold uppercase text-xs flex items-center gap-1 ${(u.worklist_categories || []).length ? "border-stone-300" : "border-amber-400 bg-amber-50 text-amber-800"}`}>
                      <ListChecks size={14} /> {(u.worklist_categories || []).length ? `${u.worklist_categories.length} types` : "None — select"}
                    </button>
                  ) : (
                    <span className="text-xs text-stone-400 font-semibold">No access</span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`user-reset-${u.username || u.id}`} onClick={() => { setResetFor(u); setNewPass(""); setError(""); }} className="h-9 px-3 border border-stone-300 font-bold uppercase text-xs flex items-center gap-1"><KeyRound size={14} /> Reset</button>
                    <button data-testid={`user-delete-${u.username || u.id}`} onClick={() => remove(u)} className="h-9 px-3 border border-red-200 text-red-700 font-bold uppercase text-xs flex items-center gap-1"><Trash2 size={14} /> Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (<tr><td colSpan={5} className="p-6 text-center text-stone-500 font-bold uppercase">No users</td></tr>)}
          </tbody>
        </table>
      </div>

      {wlFor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="worklist-access-modal">
          <div className="bg-white border border-stone-200 shadow-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-1">
              <h3 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Work List Access</h3>
              <button onClick={() => setWlFor(null)} data-testid="worklist-access-close"><X size={20} /></button>
            </div>
            <p className="text-sm text-stone-600 mb-4">Select which work list types <b>{wlFor.name}</b> ({wlFor.role}) can view. None selected = no tasks visible.</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {TASK_CATEGORIES.map((c) => {
                const sel = wlCats.includes(c);
                return (
                  <button key={c} type="button" data-testid={`wl-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setWlCats((s) => (sel ? s.filter((x) => x !== c) : [...s, c]))}
                    className={`h-10 px-3 text-xs font-bold uppercase border ${sel ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white text-stone-600 border-stone-300"}`}>
                    {c}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setWlFor(null)} className="flex-1 h-12 border border-stone-300 font-bold uppercase text-sm">Cancel</button>
              <button data-testid="worklist-access-save" onClick={saveWl} className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase text-sm">Save</button>
            </div>
          </div>
        </div>
      )}

      {resetFor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="reset-password-modal">
          <div className="bg-white border border-stone-200 shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: "'Fraunces', serif" }}>Reset Password</h3>
            <p className="text-sm text-stone-600 mb-4">Set a new password for <b>{resetFor.username || resetFor.name}</b>.</p>
            <input data-testid="reset-new-password" type="text" value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="New password" className="w-full h-12 px-3 border border-stone-200 mb-3" />
            {error && <div className="text-sm text-red-700 mb-3">{error}</div>}
            <div className="flex gap-3">
              <button data-testid="reset-cancel" onClick={() => { setResetFor(null); setError(""); }} className="flex-1 h-12 border border-stone-300 font-bold uppercase text-sm">Cancel</button>
              <button data-testid="reset-confirm" onClick={doReset} className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase text-sm">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
